import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap, catchError, of } from 'rxjs';

export interface User {
    id: string;
    login: string;
    fullName: string;
    role: 'ADMIN' | 'STUDENT';
    stream: string;
    telegramNick: string;
}

export interface LoginResponse {
    accessToken: string;
    user: User;
}

const TOKEN_KEY = 'learn_monitoring_token';
const USER_KEY = 'learn_monitoring_user';

import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly apiUrl = environment.apiUrl;

    private readonly _user = signal<User | null>(null);
    private readonly _token = signal<string | null>(null);

    readonly user = this._user.asReadonly();
    readonly isAuthenticated = computed(() => !!this._token());
    readonly isAdmin = computed(() => this._user()?.role === 'ADMIN');

    constructor(
        private readonly http: HttpClient,
        private readonly router: Router,
    ) {
        this.loadFromStorage();
    }

    private loadFromStorage(): void {
        const token = localStorage.getItem(TOKEN_KEY);
        const userJson = localStorage.getItem(USER_KEY);

        if (token && userJson) {
            this._token.set(token);
            try {
                this._user.set(JSON.parse(userJson));
            } catch {
                this.logout();
            }
        }
    }

    getToken(): string | null {
        return this._token();
    }

    login(login: string, password: string) {
        return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, { login, password })
            .pipe(
                tap((response) => {
                    this._token.set(response.accessToken);
                    this._user.set(response.user);
                    localStorage.setItem(TOKEN_KEY, response.accessToken);
                    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
                }),
            );
    }

    logout(): void {
        this._token.set(null);
        this._user.set(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        this.router.navigate(['/login']);
    }

    refreshUser() {
        return this.http.get<User>(`${this.apiUrl}/users/me`)
            .pipe(
                tap((user) => {
                    this._user.set(user);
                    localStorage.setItem(USER_KEY, JSON.stringify(user));
                }),
                catchError(() => {
                    this.logout();
                    return of(null);
                }),
            );
    }
}
